<template>
  <main-large-dialog
    :is-visible.sync="computedIsVisible"
    :title="$i18n.t('table.rziMonitoring.fito.speciesTitle')"
    dialog-width="30%"
    max-height="90vh"
    agree-text="dialog.agreeSave"
    header-shadow
    :loading="isLoading"
    :is-valid="isFormValid"
    @agree="handleSave"
  >
    <template #body>
      <v-col v-if="isFetched" cols="12">
        <v-list>
          <v-list-item>
            <v-form ref="newItemForm" v-model="isNewItemValid" style="width: 100%">
              <div class="d-flex" style="gap: 1rem">
                <span style="width: 100%">
                  <data-input
                    v-bind="inputAttributesMetadata.specie"
                    @change="$event => (newItemData.specie = $event)"
                    :value="newItemData.specie"
                  />
                </span>
                <span style="min-width: 150px">
                  <data-input
                    v-bind="inputAttributesMetadata.coverage"
                    @change="$event => (newItemData.coverage = $event)"
                    :value="newItemData.coverage"
                  />
                </span>
                <span style="width: 40px">
                  <base-rounded-button
                    icon="mdi-plus"
                    color="success"
                    @click="addNewItem"
                    :disabled="!isNewItemValid"
                  />
                </span>
              </div>
            </v-form>
          </v-list-item>
          <v-form ref="allForm" v-model="isFormValid">
            <template v-for="(specie, index) in species">
              <v-list-item v-if="specie.itemAction !== 'delete'" :key="index" class="list-element-insert">
                <div class="d-flex mt-6 align-baseline" style="gap: 1rem; width: 100%">
                  <span style="width: 100%">
                    <data-input
                      v-bind="inputAttributesMetadata.specie"
                      @change="$event => changeExistingData($event, 'specie', index)"
                      :value="specie.specie"
                    />
                  </span>
                  <span style="min-width: 150px">
                    <data-input
                      v-bind="inputAttributesMetadata.coverage"
                      @change="$event => changeExistingData($event, 'coverage', index)"
                      :value="specie.coverage"
                    />
                  </span>
                  <span style="width: 40px; min-width: 40px">
                    <v-icon @click="deleteItem(specie, index)">mdi-delete</v-icon>
                  </span>
                </div>
              </v-list-item>
            </template>
          </v-form>
        </v-list>
      </v-col>
      <v-col v-else cols="12" class="my-4">
        <main-loader />
      </v-col>
    </template>
  </main-large-dialog>
</template>

<script>
import MainLargeDialog from '@/components/MainLargeDialog';
import MainLoader from '@/components/MainLoader';
import DataInput from '@/components/DataInput';
import { utils } from '@/mixins/rziHelpers';
import { get, call } from 'vuex-pathify';
import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

export default {
  name: 'RziFitoSpecies',
  mixins: [prepareAttributesFeatureCardMixin, utils],
  components: {
    MainLargeDialog,
    MainLoader,
    DataInput,
  },
  props: {
    currentMetadata: {
      type: Object,
      required: true,
    },
    isVisible: {
      type: Boolean,
      required: true,
    },
    fitoLayerId: {
      type: Number,
      required: true,
    },
  },
  data: () => ({
    isNewItemValid: false,
    isFormValid: false,
    isFetched: false,
    isLoading: false,
    newItemData: {
      coverage: null,
      specie: null,
    },
    inputAttributesMetadata: {
      coverage: null,
      specie: null,
    },
    species: [],
  }),
  computed: {
    dataSources: get('layers/metadata'),
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
  },
  watch: {
    isVisible: {
      handler(nV) {
        if (nV) {
          this.isFetched = false;
          this.init();
        }
      },
      immediate: true,
    },
  },
  methods: {
    editMultipleDataSourceFeatures: call('layers/editMultipleDataSourceFeatures'),
    async handleSave() {
      this.isLoading = true;
      const payload = [...this.species].reduce(
        (acc, item) => {
          const properties = {
            [this.currentMetadata.fitoSpecies.attributes.layer]: this.fitoLayerId,
            [this.currentMetadata.fitoSpecies.attributes.coverage]: item.coverage,
            [this.currentMetadata.fitoSpecies.attributes.specie]: item.specie,
          };
          switch (item.itemAction) {
            case 'insert':
              if (!acc[item.itemAction]) acc[item.itemAction] = [];
              acc.insert.push({ ...properties });
              return acc;
            case 'update':
              if (!acc[item.itemAction]) acc[item.itemAction] = [];
              acc.update.push({
                fid: item.id,
                properties,
              });
              return acc;
            case 'delete':
              if (!acc[item.itemAction]) {
                acc[item.itemAction] = {
                  features_ids: [],
                };
              }
              acc[item.itemAction].features_ids.push(item.id);
              return acc;
            default:
              return acc;
          }
        },
        {
          data_source_name: this.currentMetadata.fitoSpecies.datasource,
        }
      );
      try {
        await this.editMultipleDataSourceFeatures(payload);
        this.computedIsVisible = false;
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    deleteItem(item, index) {
      if (item.itemAction === 'insert') {
        let copyList = [...this.species];
        copyList.splice(index, 1);
        this.species = [...copyList];
      } else {
        this.species[index].itemAction = 'delete';
      }
    },
    changeExistingData(value, key, index) {
      if (!this.species[index]) return;
      this.species[index] = {
        ...this.species[index],
        [key]: +value,
      };
    },
    addNewItem() {
      this.species = [
        {
          id: null,
          ...this.newItemData,
          itemAction: 'insert',
        },
        ...this.species,
      ];
      this.newItemData = {
        coverage: null,
        specie: null,
      };
      this.$refs['newItemForm']?.resetValidation();
    },
    async init() {
      try {
        const r = await this.getDataSourceFeatures({
          dataSource: this.currentMetadata.fitoSpecies.datasource,
          filters: {
            $EQUAL: {
              [`${this.currentMetadata.fitoSpecies.datasource}.${this.currentMetadata.fitoSpecies.attributes.layer}`]: {
                value: this.fitoLayerId,
              },
            },
          },
        });
        this.species = r.features
          .map(f => ({
            id: f.id,
            coverage: f.properties[this.currentMetadata.fitoSpecies.attributes.coverage],
            specie: f.properties[this.currentMetadata.fitoSpecies.attributes.specie],
            itemAction: 'update',
          }))
          .sort((a, b) => (b.coverage ?? 0) - (a.coverage ?? 0));
        this.isFetched = true;
      } catch (error) {
        console.error(error);
      }
    },
  },
  mounted() {
    this.formSchema = this.getForm(
      null,
      this.dataSources[this.currentMetadata.fitoSpecies.datasource]?.attributes_schema.attributes
    );
    this.inputAttributesMetadata = {
      coverage: this.formSchema.find(a => a.attribute === this.currentMetadata.fitoSpecies.attributes.coverage),
      specie: this.formSchema.find(a => a.attribute === this.currentMetadata.fitoSpecies.attributes.specie),
    };
  },
};
</script>

<style lang="scss" scoped>
.list-element-insert {
  @include border(all, 1px);
  border-bottom: 0;
}
.list-element-insert:last-of-type {
  @include border(bottom, 1px);
}
</style>
